<template>
  <div :class="['document-card', `document-card--${align}`, { 'notValid': hasValidationErrors }]" >
    <slot name="header">
      <DocumentCardHeader :title="title" :required="required" :additional-msg="additionalMsg" :with-template-info="!!templateUri" />
    </slot>
    <div class="card-body">
      <slot name="body">
        <DocumentCardBody ref="documentCardBody" :max-files="maxFiles" :files="filesRef" :types="types" :template-uri="templateUri" :actions="actions"
          :viewer-type="viewerType" :additional-description-mode="additionalDescriptionMode" :allowed-file-formats="allowedFileFormats" :multiple="multiple"
          :initial-type-code="selectedTypeCode" :draggable="draggable" @added:document="onAddedDocument" @updated:document="onUpdateDocument" @deleted:document="onDeleteDocument"
          @selected:document-type="onSelectedDocumentType" @drag-start:document="onDragStartDocument" />
      </slot>
      <div v-if="hasFlag(ErroresValidacion.Requerido)" class="d-flex">
        <div class="required-icon">
          <i class="fa-solid fa-circle-exclamation"></i>
        </div>
        <div class="dx-invalid-message fw-bold">
          {{ $t('documentCard.validation.requiredDocument') }}
        </div>
      </div>
      <div v-if="hasFlag(ErroresValidacion.TipoNoSeleccionado)" class="d-flex">
        <div class="required-icon">
          <i class="fa-solid fa-circle-exclamation"></i>
        </div>
        <div class="dx-invalid-message fw-bold">
          {{ $t('documentCard.validation.requiredDocumentType') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import DocumentCardHeader from './DocumentCardHeader.vue';
import DocumentCardBody from './DocumentCardBody.vue';
import type { IDocumentCardProps, IDocumentInitialState } from './models/IDocumentCardProps';
import type { CardFile, CardFileRecord } from './models/CardFile';
import _ from 'lodash';

enum ErroresValidacion {
  Ninguno = 0,
  Requerido = 1 << 0,
  TipoNoSeleccionado = 1 << 1,
}
const props = withDefaults(defineProps<IDocumentCardProps>(), {
  additionalMsg: null,
  required: false,
  additionalDescriptionMode: 'none',
  actions: () => ['open', 'download', 'delete', 'replace', 'add', 'copy'],
  align: 'vertical',
  allowedFileFormats: () => ['all'],
  maxFiles: 10,
  viewerType: 'modal',
  initialDocumentList: () => [] as IDocumentInitialState[],
  multiple: true,
  templateUri: undefined,
  draggable: false
});

const emit = defineEmits<{
  'deleted:document': [document: CardFile];
  'updated:document': [document: CardFile];
  'added:document': [document: CardFile];
  'selected:document-type': [typeCode: string];
  'drag-start:document': [document: CardFile];
}>();

const erroresValidacion = ref<ErroresValidacion>(ErroresValidacion.Ninguno);

const hasFlag = (flag: ErroresValidacion): boolean => (erroresValidacion.value & flag) !== 0;

const validate = (): void => {
  erroresValidacion.value = ErroresValidacion.Ninguno;

  if (props.types && props.types.length > 1 && (documentCardBody.value?.numFiles ?? 0) > 0 && !selectedTypeCode.value) {
    erroresValidacion.value |= ErroresValidacion.TipoNoSeleccionado;
  }

  if (props.required) {
    const activeFiles = Object.values(filesRef.value).filter(file => file.status !== 'deleted');
    if (activeFiles.length === 0) {
      erroresValidacion.value |= ErroresValidacion.Requerido;
    }
  }
}

const hasValidationErrors = computed(() => {
  return erroresValidacion.value !== ErroresValidacion.Ninguno;
});

const isValid = computed(() => {
  validate();

  return erroresValidacion.value === ErroresValidacion.Ninguno;
});

const filesRef = ref<CardFileRecord>({});
const documentCardBody = ref<InstanceType<typeof DocumentCardBody>>();
const initialTypeCode = props.types && props.types.length > 1
  ? (props.initialDocumentList?.[0]?.typeCode ?? undefined)
  : (props.initialDocumentList?.[0]?.typeCode ?? props.types?.[0]?.code);
const selectedTypeCode = ref<string | undefined>(initialTypeCode);

const clonePreservingFiles = <T,>(input: T): T => {
  return _.cloneDeepWith(input, (value) => {
    if (value instanceof File) {
      return value;
    }
    return undefined;
  });
}

const onAddedDocument = (document: CardFile) => {
  validate();
  emit('added:document', clonePreservingFiles(document));
}

const onUpdateDocument = (document: CardFile) => {
  emit('updated:document', clonePreservingFiles(document));
}

const onDeleteDocument = (document: CardFile): void => {
  validate();
  emit('deleted:document', clonePreservingFiles(document));
}

const onSelectedDocumentType = (type: string): void => {
  selectedTypeCode.value = type;
  validate();

  emit('selected:document-type', type);
};

const onDragStartDocument = (document: CardFile): void => {
  emit("drag-start:document", clonePreservingFiles(document));
};

const getFiles = (): CardFileRecord => {
  return clonePreservingFiles(filesRef.value);
}

watch(() => props.initialDocumentList, (newList, oldList) => {
  if (_.isEqual(newList, oldList)) return;
  filesRef.value = buildFilesFromInitial(newList);
}, { deep: true, immediate: true });

const removeAllFiles = (): void => {
  documentCardBody.value?.deleteAllDocuments();
}

const resetToInitial = (initial?: IDocumentInitialState[]) => {
  filesRef.value = buildFilesFromInitial(initial ?? props.initialDocumentList);
};

function buildFilesFromInitial(initial: IDocumentInitialState[]): CardFileRecord {
  setSelectedTypeCode(initial);

  return initial?.select(x => (
    {
      id: x.id,
      remoteUri: x.uri,
      fileName: x.fileName,
      file: null,
      status: "unchanged",
      description: x.description ?? null,
      typeCode: x.typeCode
    } as CardFile)).toObject(x => x.id) as CardFileRecord ?? {};
};

function setSelectedTypeCode(initial: IDocumentInitialState[]): void {
  // Si hay múltiples tipos, mantener undefined para mostrar todos los documentos
  // Solo establecer un tipo específico si hay un typeCode en los documentos iniciales
  if (props.types && props.types.length > 1) {
    selectedTypeCode.value = initial?.[0]?.typeCode ?? undefined;
  } else {
    selectedTypeCode.value = initial?.[0]?.typeCode ?? props.types?.[0]?.code;
  }
}

defineExpose({ getFiles, isValid, removeAllFiles, resetToInitial })

</script>

<style lang="scss" scoped>
.document-card {
  border: 1px solid var(--border-default-default);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
}

.document-card.document-card--horizontal {
  width: 100%;
  flex-direction: row;
}

.card-body {
  padding: 1rem;
  flex: 1 1 auto;
  overflow: auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.document-card--horizontal .card-body {
  padding: 1rem;

}

.document-card i {
  cursor: pointer;
}

.mensaje-reemplazo-documento {
  color: var(--text-error-default);
}

.required-icon {
  margin-left: 5px;
  font-size: 20px;
  cursor: pointer;
  position: relative;
  color: var(--text-error-default);
}

.tooltip {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--background-normal-dark);
  color: var(--text-default-inverse);
  padding: 5px;
  border-radius: 4px;
  white-space: nowrap;
  font-size: 12px;
  z-index: 1000000;
  margin-top: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.dx-invalid-message {
  display: block;
  margin: 5px 10px;
  color: var(--text-error-default);
  font-size: 12px;
}
</style>